import { electron } from 'lib-shared-electron';
import { Sentry } from 'lib-shared-sentry';
import { AnyAction, Middleware } from 'redux';

import { appActions } from 'app/app.actions';
import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import { AppState } from 'bootstrap.store';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { presenceActions } from 'components/presence/actions/presenceActions';

import { isWebHidSupportedInBrowser } from '../jabra/utils/isWebHidSupportedInBrowser';

import { busylightActions } from './busylight.actions';
import { getBusylightSharedWorkerConnection } from './sharedWorker/getBusylightSharedWorkerConnection';
import { busylightDeviceManager } from './utils/busylightDeviceManager';
import { Volume } from './utils/busylightDeviceManager/busylightDeviceManager.types';

/**
 * The Busylight middleware to facilitate communication between the web
 * application and the busylight devices connected via web usb.
 */
export const busylightMiddleware: () => Middleware<unknown, AppState> =
  () => (store) => {
    let initialized = false;
    let hasEstablishedCall = false;

    const initialize = () => {
      initialized = true;

      const busylightSharedWorker = getBusylightSharedWorkerConnection();

      busylightDeviceManager.on(
        'arrayBufferChange',
        (arrayBuffer: Uint8Array) => {
          const state = store.getState();
          const isPrimaryConnection = state.websocket.meta.isPrimaryConnection;

          if (!isPrimaryConnection) {
            return;
          }

          busylightSharedWorker.port.postMessage({
            type: 'busylight:sync',
            payload: { arrayBuffer },
          });
        },
      );

      busylightDeviceManager.on('deviceAdded', () => {
        store.dispatch(busylightActions.getConnectedStatus());

        const state = store.getState();
        busylightDeviceManager.setStatus(state.presence.presence.status);
      });

      busylightDeviceManager.on('deviceRemoved', () => {
        if (!busylightDeviceManager.hasConnectedDevices) {
          busylightSharedWorker.port.postMessage({
            type: 'busylight:stopSync',
          });
        }
      });

      busylightSharedWorker.port.start();
      busylightSharedWorker.port.addEventListener('message', ({ data }) => {
        if (data.type === 'busylight:sync') {
          busylightDeviceManager.update(data.payload.arrayBuffer);
        }
      });
    };

    return (next) => (action: AnyAction) => {
      try {
        if (!isWebHidSupportedInBrowser()) {
          return next(action);
        }

        const state = store.getState();

        const isWebHidBusylightEnabled = !electron.isElectron();
        if (!isWebHidBusylightEnabled) {
          return next(action);
        }

        if (!initialized) {
          initialize();
        }

        switch (action.type) {
          case busylightActions.getConnectedStatus.toString():
            store.dispatch(
              busylightActions.getConnectedStatusFulfilled({
                connected: busylightDeviceManager.hasConnectedDevices,
              }),
            );

            if (busylightDeviceManager.hasConnectedDevices) {
              busylightDeviceManager.setStatus(state.presence.presence.status);
            }
            break;
          case busylightActions.setStatus.toString():
            if (hasEstablishedCall) break;
            busylightDeviceManager.setStatus(action.payload.status);
            store.dispatch(busylightActions.setStatusFulfilled());
            break;
          case presenceActions.presenceGetSucceeded.toString():
            if (hasEstablishedCall) break;
            busylightDeviceManager.setStatus(action.payload.data.status);
            break;
          case presenceActions.presenceSetStatusSucceeded.toString():
            if (hasEstablishedCall) break;

            busylightDeviceManager.setStatus(
              action.payload.data?.presence_status_category ||
                action.payload.params?.new_status ||
                'offline',
            );
            break;
          case busylightActions.ring.toString(): {
            const { busylight: busylightConfig } = state.settings.personal;

            busylightDeviceManager.setStatus('alert', {
              volume: busylightConfig?.soundVolume as Volume,
              tone: busylightConfig?.tone,
            });

            store.dispatch(busylightActions.ringFulfilled());
            break;
          }
          case busylightActions.stop.toString(): {
            busylightDeviceManager.setStatus('offline');

            store.dispatch(busylightActions.stopFulfilled());
            break;
          }
          case busylightActions.stopFulfilled.toString(): {
            busylightDeviceManager.setStatus(state.presence.presence.status);
            break;
          }
          case conversationActions.conversationWsTransferOfferReceived.toString():
          case conversationActions.conversationWsOfferReceived.toString(): {
            const { busylight: busylightConfig } = state.settings.personal;

            busylightDeviceManager.setStatus('alert', {
              volume: busylightConfig?.soundVolume as Volume,
              tone: busylightConfig?.tone,
            });

            store.dispatch(busylightActions.ringFulfilled());
            break;
          }
          case conversationActions.conversationRespondeToOfferAccepted.toString():
          case conversationActions.conversationRespondeToOfferRejected.toString():
          case conversationActions.conversationRespondeToTransferOfferAccepted.toString():
          case conversationActions.conversationRespondeToTransferOfferRejected.toString():
          case conversationActions.conversationWsOfferCanceled.toString(): {
            busylightDeviceManager.setStatus(state.presence.presence.status);
            store.dispatch(busylightActions.stopFulfilled());
            break;
          }
          case twilioActions.twilioEstablishedCall.toString():
            hasEstablishedCall = true;
            busylightDeviceManager.setStatus('busy');
            break;
          case twilioActions.twilioDisconnectedCall.toString():
            hasEstablishedCall = false;
            busylightDeviceManager.setStatus(state.presence.presence.status);
            break;
          case appActions.logOut.fulfilled.type:
            busylightDeviceManager.setStatus('offline');
            break;
        }
      } catch (exception) {
        Sentry.captureException(
          new Error('Busylight: middleware exception caught'),
          { extra: { exception } },
        );
      }

      return next(action);
    };
  };
